/**
 * Die zehn Kapitel der Lernstrecke — Text, Grafiken und Illustrationen.
 *
 * Erzeugt aus dem von Junior Achievement Austria freigegebenen Entwurf
 * (Skills4Retail, Stand 22.08.2026). Die Inhalte stehen hier als fertiges
 * HTML und werden unverändert eingesetzt.
 *
 * **Warum HTML und nicht JSX:** Es sind rund 80 KB Fließtext und dreizehn
 * Diagramme als SVG. Eine Umschrift nach JSX wäre eine
 * fehleranfällige Handarbeit ohne Gewinn — der Inhalt ist statisch, kommt aus
 * dem eigenen Haus und wird mitkompiliert; es fließt an keiner Stelle eine
 * Eingabe hinein. Gezeigt wird er in `Concept/Chapter.jsx`.
 *
 * Die zehn Kapitelillustrationen liegen als Bilddateien daneben und kommen
 * über `illustrationen.js` herein; im HTML steht nur der Verweis darauf.
 *
 * Die Beschriftungen der Klassen (`goals`, `grid`, `note`, `art` …) gehören zu
 * `style.module.scss` in diesem Ordner; wer eine ändert, muss beides anfassen.
 */

import { ILLUSTRATIONEN } from './illustrationen'
import { MARKT_AT, FALL_ZALANDO, FALL_SHEIN_TIKTOK, FALL_DECATHLON } from './fallstudien'
import { HANDELSFORMEN } from './handelsformen'
import { PERSONAS } from './personas'
import { ico } from './symbole'

export const CHAPTERS = [
	{
		no: 1,
		title: 'Wie ein Onlineshop Geld verdient',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">01</span>
					<div>
							<h2>Wie ein Onlineshop Geld verdient</h2>
							<p class="chapter-sub">Die Begriffe und Kennzahlen, ohne die der Rest des Kurses nicht funktioniert.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[1].avif}" type="image/avif" />
					<img src="${
						ILLUSTRATIONEN[1].webp
					}" alt="Drei Jugendliche freuen sich über die steigende Verkaufskurve auf ihrem Laptop" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>die zehn Grundbegriffe des Onlinehandels erklären und richtig verwenden</li>
					<li>eine Conversion Rate und einen durchschnittlichen Warenkorbwert berechnen</li>
					<li>erklären, warum Kaufabbrüche der teuerste Moment im Shop sind</li>
				</ul>
			</div>

			<h3>Vom Besuch zum Kauf</h3>
			<p>Ein Onlineshop ist eine Maschine, die Besuche in Bestellungen verwandelt. Auf jedem
			Schritt dieser Strecke springen Leute ab. Wer weiß, <em>wo</em> sie abspringen, weiß auch,
			woran er arbeiten muss.</p>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 236" role="img" aria-label="Trichter von 1000 Besuchen über 220 Warenkörbe und 95 Kassenaufrufe zu 62 Bestellungen">
						<g class="svg-label">
							<!-- Stufen -->
							<g>
								<rect x="10" y="34" width="150" height="86" rx="10" fill="var(--wash-strong)"></rect>
								<text x="26" y="62" class="svg-small svg-soft" letter-spacing="1">BESUCHE</text>
								<text x="26" y="92" class="svg-ink" font-family="Poppins" font-size="26" font-weight="600">1.000</text>
							</g>
							<g>
								<rect x="180" y="46" width="150" height="62" rx="10" fill="var(--wash)"></rect>
								<text x="196" y="70" class="svg-small svg-soft" letter-spacing="1">WARENKORB</text>
								<text x="196" y="96" class="svg-ink" font-family="Poppins" font-size="22" font-weight="600">220</text>
							</g>
							<g>
								<rect x="350" y="54" width="150" height="46" rx="10" fill="var(--wash)"></rect>
								<text x="366" y="74" class="svg-small svg-soft" letter-spacing="1">KASSE</text>
								<text x="366" y="94" class="svg-ink" font-family="Poppins" font-size="20" font-weight="600">95</text>
							</g>
							<g>
								<rect x="520" y="58" width="150" height="38" rx="10" fill="var(--accent)"></rect>
								<text x="536" y="76" class="svg-small" fill="#fff" letter-spacing="1" opacity=".85">BESTELLUNGEN</text>
								<text x="536" y="92" fill="#fff" font-family="Poppins" font-size="17" font-weight="600">62</text>
							</g>

							<!-- Verluste -->
							<g class="svg-small" fill="var(--warn)">
								<text x="184" y="140">− 780 gehen ohne</text>
								<text x="184" y="154">Warenkorb</text>
								<text x="354" y="140">− 125 legen ab,</text>
								<text x="354" y="154">kaufen nicht</text>
								<text x="524" y="140">− 33 brechen an</text>
								<text x="524" y="154">der Kassa ab</text>
							</g>
							<g stroke="var(--warn)" stroke-width="1.4" stroke-dasharray="3 3" fill="none" opacity=".8">
								<path d="M190 122 L190 130"></path>
								<path d="M360 122 L360 130"></path>
								<path d="M530 122 L530 130"></path>
							</g>

							<!-- Pfeile -->
							<g stroke="var(--ink-faint)" stroke-width="1.6" fill="none">
								<path d="M162 77 L176 77" marker-end="url(#ar1)"></path>
								<path d="M332 77 L346 77" marker-end="url(#ar1)"></path>
								<path d="M502 77 L516 77" marker-end="url(#ar1)"></path>
							</g>
							<defs>
								<marker id="ar1" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
									<path d="M0 0 L8 4 L0 8 z" fill="var(--ink-faint)"></path>
								</marker>
							</defs>

							<!-- Rechnung -->
							<line x1="10" y1="180" x2="690" y2="180" stroke="var(--line)" stroke-width="1"></line>
							<text x="10" y="206" class="svg-small svg-faint" letter-spacing="1">CONVERSION RATE</text>
							<text x="10" y="226" class="svg-ink" font-family="Poppins" font-size="15" font-weight="600">62 ÷ 1.000 = 6,2 %</text>
							<text x="250" y="206" class="svg-small svg-faint" letter-spacing="1">Ø WARENKORBWERT</text>
							<text x="250" y="226" class="svg-ink" font-family="Poppins" font-size="15" font-weight="600">1.240 € ÷ 62 = 20 €</text>
							<text x="490" y="206" class="svg-small svg-faint" letter-spacing="1">KAUFABBRUCHQUOTE</text>
							<text x="490" y="226" class="svg-ink" font-family="Poppins" font-size="15" font-weight="600">158 ÷ 220 = 71,8 %</text>
						</g>
					</svg>
				</div>
				<figcaption>Beispielzahlen einer Junior Company nach einem Monat. Zwischen Warenkorb und Kasse geht am meisten verloren — dort lohnt sich jede Verbesserung am stärksten.</figcaption>
			</figure>

			<h3>Die zehn Begriffe</h3>
			<div class="grid two">
				<div class="card">
					<div class="karten-kopf">${ico('webshop')}<h4>Webshop</h4></div>
					<p>Die digitale Verkaufsfläche: Produkte, Warenkorb, Kassa, Bezahlung — alles an einem Ort, rund um die Uhr geöffnet.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('warenkorb')}<h4>Warenkorb (Cart)</h4></div>
					<p>Der virtuelle Korb, in dem Kund:innen sammeln, bevor sie zur Kassa gehen.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('conversion')}<h4>Conversion</h4></div>
					<p>Eine gewünschte Handlung ist passiert: Kauf, Newsletter-Anmeldung, Kontaktformular. Nicht jede Conversion ist ein Verkauf.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('prozent')}<h4>Conversion Rate</h4></div>
					<p>Anteil der Besuche mit Conversion. <b>Bestellungen ÷ Besuche.</b> Im Handel sind 1–3 % normal, alles darüber ist gut.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('muenzen')}<h4>Warenkorbwert</h4></div>
					<p>Was eine Bestellung im Schnitt einbringt. <b>Umsatz ÷ Bestellungen.</b> Steigt durch Upsell und Cross-Sell.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('aufwaerts')}<h4>Upsell</h4></div>
					<p>Das teurere Produkt vorschlagen: statt der Basis-Variante die mit besserem Material.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('dazu')}<h4>Cross-Sell</h4></div>
					<p>Das passende Zusatzprodukt vorschlagen: zur Kerze das Streichholz, zum Shirt die Tasche.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('abbruch')}<h4>Kaufabbruch</h4></div>
					<p>Ware liegt im Korb, gekauft wird nicht. Häufigste Gründe: überraschende Versandkosten, Zwang zur Registrierung, fehlende Zahlungsart.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('kassa')}<h4>Checkout</h4></div>
					<p>Der letzte Abschnitt: Adresse, Versandart, Zahlung, Bestätigung. Jeder zusätzliche Schritt kostet Bestellungen.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('zurueck')}<h4>Retargeting</h4></div>
					<p>Erneute Ansprache von Leuten, die schon da waren — per E-Mail an den liegengebliebenen Warenkorb oder per Anzeige.</p>
				</div>
			</div>

			<div class="note">
				<span class="ico i-merke">MERKE</span>
				<span>Es gibt zwei Wege zu mehr Umsatz: <b>mehr Besuche</b> (Marketing) oder
				<b>mehr aus den vorhandenen Besuchen machen</b> (Conversion Rate und Warenkorbwert).
				Der zweite Weg ist fast immer der billigere — er kostet kein Werbebudget.</span>
			</div>

			<h3>Wer verkauft an wen? Die vier Grundformen</h3>
			<p>„Onlinehandel“ ist nicht eine Sache. Wer die Käuferseite kennt, weiß, wie er
			schreibt, was er verlangt und welche Regeln gelten. Für Junior Companies ist fast
			immer die erste Form gemeint — die anderen zu kennen hilft trotzdem, weil ihr
			Lieferanten und Partner darin einordnen könnt.</p>

			<div class="grid two">
				<div class="card">
					<figure class="karten-bild">
						<picture>
							<source srcset="${HANDELSFORMEN.b2c.avif}" type="image/avif" />
							<img src="${HANDELSFORMEN.b2c.webp}" alt="Ein Laden reicht einer einzelnen Kundin ein Paket" />
						</picture>
					</figure>
					<span class="tag">B2C</span>
					<h4>Firma verkauft an Privatperson</h4>
					<p>Der Normalfall im Onlinehandel — und eurer. Einzelne Stücke, kleine Beträge,
					viele Käufer:innen. Es gilt das volle Konsumentenschutzrecht: Preise inklusive
					Umsatzsteuer, 14 Tage Rücktritt, Impressum (Kapitel 3).</p>
				</div>
				<div class="card">
					<figure class="karten-bild">
						<picture>
							<source srcset="${HANDELSFORMEN.b2b.avif}" type="image/avif" />
							<img src="${
								HANDELSFORMEN.b2b.webp
							}" alt="Zwischen zwei Firmengebäuden fährt eine Palette mit Kartons, daneben eine Rechnung" />
						</picture>
					</figure>
					<span class="tag">B2B</span>
					<h4>Firma verkauft an Firma</h4>
					<p>Größere Mengen, Rechnung statt Sofortzahlung, Preise oft netto. Kein
					Rücktrittsrecht. Für euch als <em>Käufer</em> relevant: Wenn ihr Material bei
					einem Großhändler bezieht, seid ihr im B2B unterwegs.</p>
				</div>
				<div class="card">
					<figure class="karten-bild">
						<picture>
							<source srcset="${HANDELSFORMEN.c2c.avif}" type="image/avif" />
							<img src="${HANDELSFORMEN.c2c.webp}" alt="Zwei Privatpersonen tauschen an einem Flohmarktstand eine gebrauchte Jacke" />
						</picture>
					</figure>
					<span class="tag">C2C</span>
					<h4>Privatperson verkauft an Privatperson</h4>
					<p>Willhaben, Vinted, Flohmarktgruppen. Kein Gewerbe, keine Gewährleistung wie
					im Handel. <b>Achtung:</b> Eine Junior Company ist <em>kein</em> C2C — ihr
					handelt als Unternehmen und tragt dessen Pflichten, auch wenn ihr über eine
					Plattform verkauft, auf der sonst Private unterwegs sind.</p>
				</div>
				<div class="card">
					<figure class="karten-bild">
						<picture>
							<source srcset="${HANDELSFORMEN.c2b.avif}" type="image/avif" />
							<img src="${HANDELSFORMEN.c2b.webp}" alt="Ein Fotograf bietet einem Firmengebäude ein Bild und eine Bewertung an" />
						</picture>
					</figure>
					<span class="tag">C2B</span>
					<h4>Privatperson verkauft an Firma</h4>
					<p>Die seltenste Form: Jemand bietet einer Firma etwas an — ein Foto, eine
					Bewertung, eine Dienstleistung. Für euch praktisch nur als Randfall, wenn ihr
					etwa Bildmaterial von Mitschüler:innen einkauft.</p>
				</div>
			</div>

			<h3>Eigener Shop oder Marktplatz?</h3>
			<p>Die zweite Grundentscheidung. Sie bestimmt, wie viel Arbeit ihr habt, wie viele
			Leute euch finden und wie viel vom Preis bei euch bleibt.</p>

			<div class="tablewrap">
				<table>
					<thead>
						<tr><th>&nbsp;</th><th>Eigener Shop</th><th>Marktplatz</th></tr>
					</thead>
					<tbody>
						<tr><td><b>Wer euch findet</b></td><td>nur wer euch kennt oder sucht</td><td>Publikum ist schon da</td></tr>
						<tr><td><b>Was es kostet</b></td><td>Shop-Gebühr, dafür keine Verkaufsprovision</td><td>meist 5–15 % vom Verkaufspreis</td></tr>
						<tr><td><b>Wem die Kundin gehört</b></td><td>euch — ihr habt die Adresse</td><td>der Plattform</td></tr>
						<tr><td><b>Wie es aussieht</b></td><td>euer Auftritt, eure Regeln</td><td>Vorlage der Plattform</td></tr>
						<tr><td><b>Aufwand am Anfang</b></td><td>hoch: aufsetzen, gestalten, bekannt machen</td><td>niedrig: einstellen und los</td></tr>
					</tbody>
				</table>
			</div>

			<div class="note">
				<span class="ico i-euch">FÜR EUCH</span>
				<span>Beides schließt sich nicht aus, und für eine Junior Company ist die
				Kombination oft die beste: der eigene Shop als Zuhause — dort steht eure
				Geschichte —, dazu ein Marktplatz oder ein Schulflohmarkt für die Reichweite.
				Wichtig ist nur <b>eine</b> Bestandsliste für beide (Kapitel 10).</span>
			</div>

			<h3>Dropshipping — und warum es für euch selten passt</h3>
			<p>Beim Dropshipping habt ihr die Ware nie in der Hand: Bestellt jemand bei euch,
			gebt ihr die Bestellung an einen Lieferanten weiter, der direkt an die Kundin
			verschickt. Kein Lager, kein Kapital, kein Risiko — klingt ideal.</p>
			<ul class="bullets">
				<li><b>Ihr haftet trotzdem.</b> Gegenüber der Käuferin seid ihr die Verkäuferin:
				Lieferzeit, Gewährleistung, Rücknahme — alles eures, auch wenn der Fehler beim
				Lieferanten liegt.</li>
				<li><b>Ihr seht die Ware nie.</b> Qualität, Verpackung, Beilage — ihr erfahrt
				davon, wenn sich jemand beschwert.</li>
				<li><b>Die Marge ist dünn</b>, weil der Lieferant den Versand mitverdient.</li>
				<li><b>Und das Wichtigste:</b> Der Sinn einer Junior Company ist, ein Produkt zu
				machen, zu kalkulieren und zu verkaufen. Wer nur weiterleitet, lernt den halben
				Teil nicht.</li>
			</ul>
			<p>Als <em>Ergänzung</em> kann es passen — etwa für ein Zusatzprodukt, das ihr nicht
			selbst herstellen könnt. Als Geschäftsmodell für die ganze Firma nicht.</p>

			<h3>Rechnen mit euren eigenen Zahlen</h3>
			<p>Angenommen, eure Junior Company hat im ersten Monat 1.000 Besuche, 62 Bestellungen
			und 1.240 € Umsatz. Dann gilt: Conversion Rate 6,2 %, Warenkorbwert 20 €. Erhöht ihr
			allein den Warenkorbwert durch ein Cross-Sell-Produkt um 3 €, sind das
			<b>186 € Mehrumsatz</b> im Monat — ohne einen einzigen zusätzlichen Besuch.</p>

			${MARKT_AT}`
	},
	{
		no: 2,
		title: 'Wer ihr seid und für wen',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [1, 2],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">02</span>
					<div>
							<h2>Wer ihr seid und für wen</h2>
							<p class="chapter-sub">Unternehmensprofil, Alleinstellungsmerkmal und die Buyer Persona.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[2].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[2].webp}" alt="Zwei Jugendliche ordnen Karten mit Kundengesichtern an einer Pinnwand" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>das Kundenproblem eurer Junior Company in einem Satz benennen</li>
					<li>ein echtes Alleinstellungsmerkmal von einer Floskel unterscheiden</li>
					<li>eine Buyer Persona mit allen fünf Bestandteilen schreiben</li>
				</ul>
			</div>

			<h3>Value Proposition: das Nutzenversprechen</h3>
			<p>Eine Value Proposition ist kein Werbespruch, sondern eine Antwort auf drei Fragen:
			<b>Wer</b> hat <b>welches Problem</b>, und <b>warum löst ausgerechnet ihr es besser</b>?
			Wenn eine dieser drei Angaben fehlt, ist es keine Value Proposition.</p>

			<div class="grid two">
				<div class="card">
					<span class="tag warnT">Schwach</span>
					<h4>„Wir verkaufen hochwertige, nachhaltige Notizbücher zu fairen Preisen."</h4>
					<p>Kein Adressat, kein Problem, keine Abgrenzung. „Hochwertig", „nachhaltig" und
					„fair" behaupten alle. Das ist der typische 3-Punkte-Text.</p>
				</div>
				<div class="card">
					<span class="tag okT">Stark</span>
					<h4>„Maturaklassen brauchen ein Notizbuch, das ein Jahr Schultasche übersteht. Unseres wird in der Schulwerkstatt aus Buchbinderpappe gefertigt und kostet 12 €."</h4>
					<p>Adressat, Problem, Lösung, Beleg, Preis. Das ist ein 8-Punkte-Text.</p>
				</div>
			</div>

			<h3>USP: worin ihr wirklich anders seid</h3>
			<p>Das Alleinstellungsmerkmal (Unique Selling Proposition) muss drei Tests bestehen:
			Es ist <b>wahr</b>, es ist <b>für Kund:innen wichtig</b>, und die Konkurrenz kann es
			<b>nicht einfach behaupten</b>. Für eine Junior Company sind die realistischen
			USP-Quellen fast immer dieselben:</p>
			<ul class="bullets">
				<li><b>Herkunft:</b> hier in der Region, in unserer Schule, von uns selbst gemacht.</li>
				<li><b>Zweck:</b> ein Teil des Gewinns geht an ein konkretes Projekt (mit Namen, nicht „an Bedürftige").</li>
				<li><b>Anpassung:</b> personalisierbar — Name, Klasse, Farbe, Gravur.</li>
				<li><b>Zugang:</b> Abholung im Schulhaus, Lieferung am nächsten Tag im Ort.</li>
				<li><b>Nische:</b> ein Produkt, das für diese eine Zielgruppe gemacht ist und sonst niemand anbietet.</li>
			</ul>

			<h3>Die Buyer Persona</h3>
			<p>Eine Buyer Persona ist eine erfundene, aber realistische Einzelperson, die für eure
			typische Kundschaft steht. Sie hat einen Namen und ein Alter — das klingt albern, wirkt
			aber: Über „Lena, 17" lässt sich streiten, über „unsere Zielgruppe" nicht. Legt euch
			zwei an, wenn zwei verschiedene Leute bei euch kaufen.</p>

			<!--
				Zwei Personas, aber immer nur eine auf dem Schirm: Nebeneinander
				gestellt las man beide halb; hintereinander gelegt vergleicht man
				sie wirklich. Umgeschaltet wird oben — mit der Maus über die zwei
				Reiter, mit der Tastatur über die Pfeiltasten.

				Das kommt ohne eine Zeile JavaScript aus: Zwei Radioknöpfe mit
				demselben Namen sind eine Gruppe, und in einer Radiogruppe
				wechseln die Pfeiltasten von Haus aus die Auswahl. Der Kapiteltext
				ist festes HTML (siehe oben) — ein Schalter, der Skript bräuchte,
				ginge hier nicht.
			-->
			<figure class="reveal personas">
				<input class="persona-wahl" type="radio" name="persona" id="persona-lena" aria-label="Lena, 17" checked />
				<input class="persona-wahl" type="radio" name="persona" id="persona-michael" aria-label="Michael, 42" />

				<div class="persona-nav">
					<label class="persona-reiter" for="persona-lena">
						<picture>
							<source srcset="${PERSONAS.lena.avif}" type="image/avif" />
							<img src="${PERSONAS.lena.webp}" alt="" />
						</picture>
						<span>
							<b>Lena, 17</b>
							<small>kauft für sich selbst</small>
						</span>
					</label>
					<label class="persona-reiter" for="persona-michael">
						<picture>
							<source srcset="${PERSONAS.michael.avif}" type="image/avif" />
							<img src="${PERSONAS.michael.webp}" alt="" />
						</picture>
						<span>
							<b>Michael, 42</b>
							<small>kauft für seine Klasse</small>
						</span>
					</label>
					<span class="persona-taste"><kbd>←</kbd><kbd>→</kbd> wechseln</span>
				</div>

				<div class="persona-buehne">
					<div class="card persona">
						<div class="persona-kopf">
							<picture>
								<source srcset="${PERSONAS.lena.avif}" type="image/avif" />
								<img class="persona-bild" src="${PERSONAS.lena.webp}" alt="Gezeichnetes Porträt von Lena" />
							</picture>
							<div>
								<span class="persona-marke">Persona 1</span>
								<h4>Lena, 17</h4>
								<p class="persona-rolle">Schülerin, 5. Klasse HAK Graz · kauft für sich selbst</p>
							</div>
						</div>
						<dl class="persona-liste">
							<dt>${ico('person', 'klein')}Demografie</dt>
							<dd>17 Jahre, Graz, rund 90 € Taschengeld im Monat</dd>
							<dt>${ico('herz', 'klein')}Interessen</dt>
							<dd>Handlettering, Bullet Journal, Second Hand</dd>
							<dt>${ico('warenkorb', 'klein')}Kaufverhalten</dt>
							<dd>kauft am Handy, abends, vergleicht Preise, zahlt am liebsten mit Klarna oder Sofort</dd>
							<dt>${ico('funk', 'klein')}Kanäle</dt>
							<dd class="kanaele">
								<span class="kanal">Instagram</span>
								<span class="kanal">TikTok</span>
								<span class="kanal aus">Google-Suche</span>
								<span class="kanal aus">E-Mail</span>
							</dd>
							<dt>${ico('achtung', 'klein')}Problem — unsere Antwort</dt>
							<dd class="loesung">„Meine Notizbücher fallen nach zwei Monaten auseinander."
							<b>→ fadengeheftet, aus Buchbinderpappe, 12 € — hält das ganze Schuljahr.</b></dd>
						</dl>
					</div>
					<div class="card persona">
						<div class="persona-kopf">
							<picture>
								<source srcset="${PERSONAS.michael.avif}" type="image/avif" />
								<img class="persona-bild" src="${PERSONAS.michael.webp}" alt="Gezeichnetes Porträt von Michael" />
							</picture>
							<div>
								<span class="persona-marke">Persona 2</span>
								<h4>Michael, 42</h4>
								<p class="persona-rolle">Lehrer an einer HAK · kauft für seine Klasse</p>
							</div>
						</div>
						<dl class="persona-liste">
							<dt>${ico('person', 'klein')}Demografie</dt>
							<dd>42 Jahre, Linz, bestellt aus dem Klassenbudget der Schule</dd>
							<dt>${ico('herz', 'klein')}Interessen</dt>
							<dd>Projektunterricht, regionale Herkunft, Nachhaltigkeit im Lehrplan</dd>
							<dt>${ico('warenkorb', 'klein')}Kaufverhalten</dt>
							<dd>sucht am Rechner, vergleicht zwei Anbieter, braucht eine Rechnung und einen festen Liefertermin</dd>
							<dt>${ico('funk', 'klein')}Kanäle</dt>
							<dd class="kanaele">
								<span class="kanal">Google-Suche</span>
								<span class="kanal">E-Mail</span>
								<span class="kanal aus">TikTok</span>
								<span class="kanal aus">Instagram</span>
							</dd>
							<dt>${ico('achtung', 'klein')}Problem — unsere Antwort</dt>
							<dd class="loesung">„Ich brauche 25 gleiche Bücher bis zum Schulanfang, mit Rechnung."
							<b>→ Sammelbestellung ab 20 Stück zu 10 €, Rechnung mit 14 Tagen Ziel.</b></dd>
						</dl>
					</div>
				</div>
				<figcaption>Fünf Bestandteile je Persona: Demografie, Interessen, Kaufverhalten, Kanäle,
				Problem. Zwei Personas lohnen sich, sobald zwei verschiedene Leute kaufen — Lena und
				Michael wollen dasselbe Notizbuch, sind aber auf keinem gemeinsamen Kanal erreichbar.
				Was ihr über die Kanäle wisst, entscheidet später über Kapitel 9.</figcaption>
			</figure>

			<div class="note">
				<span class="ico i-tipp">TIPP</span>
				<span>Ihr müsst die Persona nicht erfinden. Fragt zehn Leute aus eurer Zielgruppe
				in der Pause: Was nervt euch an dem Produkt, das ihr gerade benutzt? Wie viel würdet
				ihr ausgeben? Wo habt ihr das zuletzt gekauft? Drei Antworten davon im Konzept
				zitiert, und ihr seid im 9-Punkte-Bereich.</span>
			</div>`
	},
	{
		no: 3,
		title: 'Was das Gesetz von euch verlangt',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [3],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">03</span>
					<div>
							<h2>Was das Gesetz von euch verlangt</h2>
							<p class="chapter-sub">Impressum, Datenschutz, AGB und das Rücktrittsrecht — für einen Shop in Österreich.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[3].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[3].webp}" alt="Drei Jugendliche stempeln ein Dokument, daneben ein Schutzschild mit Schloss" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>die vier Pflichtbausteine jeder Verkaufswebsite aufzählen</li>
					<li>erklären, was das 14-tägige Rücktrittsrecht bedeutet und wann es nicht gilt</li>
					<li>einen Cookie-Banner beurteilen, der die Einwilligung nicht wirklich einholt</li>
				</ul>
			</div>

			<p>Ein Onlineshop ist kein rechtsfreier Raum, auch nicht als Schulprojekt. Vier Dinge
			müssen sitzen, bevor der erste Verkauf stattfindet.</p>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 220" role="img" aria-label="Vier Pflichtbausteine: Impressum, Datenschutz, AGB und Informationspflichten, Rücktrittsrecht">
						<g class="svg-label">
							<g>
								<rect x="4" y="4" width="168" height="212" rx="12" fill="var(--surface-2)"></rect>
								<rect x="4" y="4" width="168" height="6" rx="3" fill="var(--accent)"></rect>
								<text x="22" y="42" class="svg-ink" font-family="Poppins" font-size="15" font-weight="600">Impressum</text>
								<text x="22" y="62" class="svg-small svg-faint">§ 5 ECG, § 25 MedienG</text>
								<g class="svg-small svg-soft">
									<text x="22" y="90">Name der Junior Company</text>
									<text x="22" y="108">Schule + Anschrift</text>
									<text x="22" y="126">E-Mail-Adresse</text>
									<text x="22" y="144">Vertretungsberechtigte</text>
									<text x="22" y="162">Unternehmensgegenstand</text>
									<text x="22" y="180">Aufsichtsbehörde/Schule</text>
								</g>
							</g>
							<g>
								<rect x="180" y="4" width="168" height="212" rx="12" fill="var(--surface-2)"></rect>
								<rect x="180" y="4" width="168" height="6" rx="3" fill="var(--accent)"></rect>
								<text x="198" y="42" class="svg-ink" font-family="Poppins" font-size="15" font-weight="600">Datenschutz</text>
								<text x="198" y="62" class="svg-small svg-faint">DSGVO + DSG</text>
								<g class="svg-small svg-soft">
									<text x="198" y="90">Welche Daten, wozu</text>
									<text x="198" y="108">Rechtsgrundlage</text>
									<text x="198" y="126">Speicherdauer</text>
									<text x="198" y="144">Auskunft &amp; Löschung</text>
									<text x="198" y="162">Weitergabe (Versand,</text>
									<text x="198" y="176">Zahlung, Analyse)</text>
									<text x="198" y="196" fill="var(--warn)">Cookie-Einwilligung!</text>
								</g>
							</g>
							<g>
								<rect x="356" y="4" width="168" height="212" rx="12" fill="var(--surface-2)"></rect>
								<rect x="356" y="4" width="168" height="6" rx="3" fill="var(--accent)"></rect>
								<text x="374" y="42" class="svg-ink" font-family="Poppins" font-size="15" font-weight="600">AGB &amp; Infos</text>
								<text x="374" y="62" class="svg-small svg-faint">FAGG, PrAG</text>
								<g class="svg-small svg-soft">
									<text x="374" y="90">Bruttopreise inkl. USt</text>
									<text x="374" y="108">Versandkosten vorab</text>
									<text x="374" y="126">Lieferfrist</text>
									<text x="374" y="144">Zahlungsarten</text>
									<text x="374" y="162">Bestellbutton beschriftet</text>
									<text x="374" y="176">(„zahlungspflichtig</text>
									<text x="374" y="190">bestellen")</text>
								</g>
							</g>
							<g>
								<rect x="532" y="4" width="164" height="212" rx="12" fill="var(--wash)"></rect>
								<rect x="532" y="4" width="164" height="6" rx="3" fill="var(--warn)"></rect>
								<text x="550" y="42" class="svg-ink" font-family="Poppins" font-size="15" font-weight="600">Rücktritt</text>
								<text x="550" y="62" class="svg-small svg-faint">FAGG — Fernabsatz</text>
								<text x="550" y="100" class="svg-ink" font-family="Poppins" font-size="30" font-weight="700">14</text>
								<text x="590" y="100" class="svg-soft">Tage</text>
								<g class="svg-small svg-soft">
									<text x="550" y="128">ohne Angabe von</text>
									<text x="550" y="142">Gründen</text>
									<text x="550" y="166">Fehlt die Belehrung,</text>
									<text x="550" y="180">verlängert sich die</text>
									<text x="550" y="194">Frist erheblich.</text>
								</g>
							</g>
						</g>
					</svg>
				</div>
				<figcaption>ECG = E-Commerce-Gesetz, FAGG = Fern- und Auswärtsgeschäfte-Gesetz, PrAG = Preisauszeichnungsgesetz. Die genaue Ausgestaltung besprecht ihr mit eurer Lehrkraft.</figcaption>
			</figure>

			<h3>Rücktrittsrecht: 14 Tage, aber nicht immer</h3>
			<p>Wer als Verbraucher:in im Internet bestellt, darf binnen 14 Tagen ohne Begründung
			zurücktreten. Die Frist beginnt bei Waren mit der Lieferung, nicht mit der Bestellung.
			Ausnahmen, die für Junior Companies wirklich vorkommen:</p>
			<ul class="bullets">
				<li><b>Maßanfertigungen</b> — der Pullover mit dem eingestickten Namen der Kundin.</li>
				<li><b>Schnell verderbliche Waren</b> — der selbstgebackene Kuchen.</li>
				<li><b>Versiegelte Hygieneartikel</b>, wenn die Versiegelung geöffnet wurde.</li>
				<li><b>Veranstaltungen zu einem bestimmten Termin</b> — das Ticket für euren Schulball.</li>
			</ul>

			<h3>Cookies und Einwilligung</h3>
			<p>Technisch notwendige Cookies — Warenkorb, Login, Spracheinstellung — dürfen ohne
			Nachfrage gesetzt werden. Alles andere, also Analyse und Werbung, braucht eine
			<b>aktive Einwilligung</b>. Aktiv heißt: vorher nichts angehakt, Ablehnen genauso
			leicht wie Zustimmen, und kein Cookie wird gesetzt, bevor geklickt wurde.</p>

			<div class="note warnN">
				<span class="ico i-warn">TYPISCHER FEHLER</span>
				<span>Ein Banner mit einem großen grünen „Alle akzeptieren" und einem grauen
				Textlink „Einstellungen" holt keine gültige Einwilligung ein. Ebenso wenig ein
				Banner, hinter dem Google Analytics längst mitzählt.</span>
			</div>

			<h3>Ethik: was erlaubt ist und trotzdem nicht in Ordnung</h3>
			<ul class="bullets">
				<li><b>Fake-Bewertungen</b> — selbst geschriebene Fünf-Sterne-Rezensionen sind irreführende Geschäftspraxis, nicht Marketing.</li>
				<li><b>Künstliche Knappheit</b> — „nur noch 2 Stück!", obwohl 200 im Lager liegen.</li>
				<li><b>Durchgestrichene Fantasiepreise</b>, die nie verlangt wurden.</li>
				<li><b>Fremde Bilder</b> aus dem Netz — Produktfotos und Musik sind urheberrechtlich geschützt. Selbst fotografieren oder lizenzfreie Quellen verwenden.</li>
				<li><b>Werbung an Kinder</b> ohne Kennzeichnung, wenn ihr mit Influencer:innen zusammenarbeitet: Kooperationen müssen als Werbung erkennbar sein.</li>
			</ul>`
	},
	{
		no: 4,
		title: 'Was ihr verkauft — und woher',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [4],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">04</span>
					<div>
							<h2>Was ihr verkauft — und woher</h2>
							<p class="chapter-sub">Produktauswahl, Beschaffung, Marge und Qualitätssicherung.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[4].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[4].webp}" alt="Zwei Jugendliche prüfen eine selbst genähte Tasche und eine Kerze an der Werkbank" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>ein Produkt anhand von sechs Kriterien beurteilen, bevor ihr euch festlegt</li>
					<li>eine Marge ausrechnen und sagen, ob sie reicht</li>
					<li>die drei Beschaffungswege einer Junior Company gegeneinander abwägen</li>
				</ul>
			</div>

			<h3>Sechs Fragen vor der Entscheidung</h3>
			<p>Die Produktwahl entscheidet über fast alles, was danach kommt: Preis, Marge, Versand,
			Retouren, Bildsprache. Umgekehrt lässt sich ein schlecht gewähltes Produkt durch kein
			Marketing retten. Prüft jede Idee gegen diese sechs Fragen.</p>

			<ol class="steps">
				<li><b>Wie stark ist die Konkurrenz?</b> Mode und Alltagsartikel stehen gegen internationale Anbieter mit Preisen, die ihr nie erreicht. In einer Nische seid ihr allein.</li>
				<li><b>Physisch oder digital?</b> Physische Produkte brauchen Material, Lager und Versand. Digitale Produkte — E-Book, Vorlagen, Onlinekurs — werden einmal erstellt und beliebig oft verkauft.</li>
				<li><b>Stimmt die Marge?</b> Verkaufspreis minus alle Kosten muss deutlich über null liegen, sonst arbeitet ihr gratis.</li>
				<li><b>Einmalkauf oder Wiederkauf?</b> Verbrauchsgüter (Seife, Kekse, Kerzen) bringen dieselben Leute zurück. Ein Möbelstück kaufen sie einmal.</li>
				<li><b>Lässt es sich zeigen und erklären?</b> Wenn ihr das Produkt nicht in einem Foto und drei Sätzen verständlich machen könnt, wird das Marketing teuer.</li>
				<li><b>Welche Auflagen gibt es?</b> Lebensmittel brauchen Kennzeichnung und Hygiene, Kosmetik eine Sicherheitsbewertung, Kinderspielzeug besondere Normen.</li>
			</ol>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 300" role="img" aria-label="Matrix aus Marge und Aufwand mit vier Feldern und eingezeichneten Produktbeispielen">
						<g class="svg-label">
							<rect x="70" y="20" width="290" height="115" rx="9" fill="var(--warn-wash)"></rect>
							<rect x="364" y="20" width="290" height="115" rx="9" fill="var(--ok-wash)"></rect>
							<rect x="70" y="139" width="290" height="115" rx="9" fill="var(--surface-2)"></rect>
							<rect x="364" y="139" width="290" height="115" rx="9" fill="var(--warn-wash)"></rect>

							<text x="84" y="42" class="svg-small svg-faint" letter-spacing="1">VIEL ARBEIT, GUTE MARGE</text>
							<text x="378" y="42" class="svg-small svg-faint" letter-spacing="1">WENIG ARBEIT, GUTE MARGE</text>
							<text x="84" y="161" class="svg-small svg-faint" letter-spacing="1">VIEL ARBEIT, DÜNNE MARGE</text>
							<text x="378" y="161" class="svg-small svg-faint" letter-spacing="1">WENIG ARBEIT, DÜNNE MARGE</text>

							<g>
								<circle cx="150" cy="82" r="7" fill="var(--accent)"></circle>
								<text x="164" y="87" class="svg-soft">Handgenähte Taschen</text>
								<circle cx="450" cy="72" r="7" fill="var(--accent)"></circle>
								<text x="464" y="77" class="svg-soft">Digitale Lernvorlagen</text>
								<circle cx="470" cy="108" r="7" fill="var(--accent)"></circle>
								<text x="484" y="113" class="svg-soft">Personalisierte Gravur</text>
								<circle cx="160" cy="205" r="7" fill="var(--ink-faint)"></circle>
								<text x="174" y="210" class="svg-soft">Frische Backwaren</text>
								<circle cx="470" cy="200" r="7" fill="var(--ink-faint)"></circle>
								<text x="484" y="205" class="svg-soft">Weiterverkauf von Standardware</text>
							</g>

							<line x1="70" y1="137" x2="654" y2="137" stroke="var(--line)" stroke-width="1.5" stroke-dasharray="4 4"></line>
							<line x1="362" y1="20" x2="362" y2="254" stroke="var(--line)" stroke-width="1.5" stroke-dasharray="4 4"></line>

							<text x="26" y="137" class="svg-small svg-faint" letter-spacing="1" transform="rotate(-90 26 137)" text-anchor="middle">MARGE</text>
							<text x="52" y="34" class="svg-small svg-faint" transform="rotate(-90 52 34)" text-anchor="start">hoch</text>
							<text x="52" y="240" class="svg-small svg-faint" transform="rotate(-90 52 240)" text-anchor="end">niedrig</text>

							<text x="362" y="292" class="svg-small svg-faint" letter-spacing="1" text-anchor="middle">AUFWAND JE VERKAUFTEM STÜCK</text>
							<text x="70" y="272" class="svg-small svg-faint">hoch</text>
							<text x="654" y="272" class="svg-small svg-faint" text-anchor="end">niedrig</text>
						</g>
					</svg>
				</div>
				<figcaption>Rechts oben ist der Zielbereich. Links unten wird es gefährlich: viel Arbeit für wenig Ertrag ist der häufigste Grund, warum Junior Companies im Frühjahr aufgeben.</figcaption>
			</figure>

			<h3>Die Marge ausrechnen</h3>
			<p>Rechnet ehrlich. Zur Marge gehören nicht nur Material, sondern auch Verpackung,
			Versand (falls ihr ihn nicht getrennt verrechnet), Zahlungsgebühren und der Anteil an
			Fixkosten wie Shopmiete.</p>

			<div class="tablewrap">
				<table>
					<thead><tr><th>Position</th><th style="width:120px">Betrag</th><th>Anmerkung</th></tr></thead>
					<tbody>
						<tr><td>Verkaufspreis (brutto)</td><td class="num">12,00 €</td><td>Was die Kundin zahlt</td></tr>
						<tr><td>− Material</td><td class="num">3,40 €</td><td>Pappe, Papier, Garn</td></tr>
						<tr><td>− Verpackung</td><td class="num">0,60 €</td><td>Karton, Füllmaterial, Aufkleber</td></tr>
						<tr><td>− Zahlungsgebühr</td><td class="num">0,35 €</td><td>rund 1,5 % + Fixbetrag je Transaktion</td></tr>
						<tr><td>− Anteil Fixkosten</td><td class="num">1,10 €</td><td>Shopmiete ÷ erwartete Stückzahl</td></tr>
						<tr><td><b>= Deckungsbeitrag</b></td><td class="num"><b>6,55 €</b></td><td>54,6 % vom Verkaufspreis</td></tr>
					</tbody>
				</table>
			</div>

			<div class="note">
				<span class="ico i-regel">FAUSTREGEL</span>
				<span>Bleibt nach allen Abzügen weniger als ein Drittel des Verkaufspreises übrig,
				stimmt entweder der Preis nicht oder das Produkt nicht. Und: Die eigene Arbeitszeit
				ist in dieser Rechnung noch gar nicht enthalten.</span>
			</div>

			<h3>Drei Beschaffungswege</h3>
			<div class="grid three">
				<div class="card">
					<span class="tag">Eigenproduktion</span>
					<h4>Selbst herstellen</h4>
					<p>Beste Marge, glaubwürdigste Geschichte, volle Kontrolle über Qualität.</p>
					<ul><li>Braucht viel Zeit im Team</li><li>Schwer skalierbar bei plötzlicher Nachfrage</li><li>Gleichbleibende Qualität ist Arbeit</li></ul>
				</div>
				<div class="card">
					<span class="tag">Großhandel</span>
					<h4>Fertig einkaufen</h4>
					<p>Schnell startklar, planbare Mengen, kein Produktionsrisiko.</p>
					<ul><li>Dünnere Marge</li><li>Mindestabnahmemengen binden Kapital</li><li>Andere verkaufen dasselbe</li></ul>
				</div>
				<div class="card">
					<span class="tag">Regional</span>
					<h4>Lokale Partner</h4>
					<p>Kurze Wege, gute Geschichte, oft Bereitschaft, ein Schulprojekt zu unterstützen.</p>
					<ul><li>Begrenzte Menge</li><li>Abhängigkeit von einem Betrieb</li><li>Preise oft verhandelbar — nachfragen!</li></ul>
				</div>
			</div>

			<h3>Qualität sichern</h3>
			<ul class="bullets">
				<li><b>Musterstück festlegen.</b> Ein Exemplar ist die Referenz — alles muss so aussehen.</li>
				<li><b>Stichproben.</b> Jedes zehnte Stück vor dem Versand prüfen, Ergebnis notieren.</li>
				<li><b>Zuständigkeit benennen.</b> Eine Person im Team ist für Qualität verantwortlich, nicht „alle".</li>
				<li><b>Reklamationen zählen.</b> Häufen sich dieselben Mängel, liegt es am Prozess, nicht am Pech.</li>
			</ul>`
	},
	{
		no: 5,
		title: 'Der Shop und seine Struktur',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [5],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">05</span>
					<div>
							<h2>Der Shop und seine Struktur</h2>
							<p class="chapter-sub">Shopsystem wählen, Seiten planen, Technik im Griff behalten.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[5].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[5].webp}" alt="Drei Jugendliche stecken bunte Karten zu einem Seitenaufbau zusammen" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>Miet-, Open-Source- und Eigenbau-Lösungen begründet gegeneinander abwägen</li>
					<li>eine Sitemap für euren Shop zeichnen</li>
					<li>erklären, warum Ladezeit und mobile Darstellung über Umsatz entscheiden</li>
				</ul>
			</div>

			<h3>Drei Wege zum eigenen Shop</h3>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 230" role="img" aria-label="Vergleich von Mietshop, Open Source und Eigenentwicklung nach Startkosten, Zeit, Flexibilität und Technikbedarf">
						<g class="svg-label">
							<text x="150" y="20" class="svg-ink" font-family="Poppins" font-size="14" font-weight="600" text-anchor="middle">Mietshop</text>
							<text x="150" y="36" class="svg-small svg-faint" text-anchor="middle">Shopify, Wix, Jimdo</text>
							<text x="370" y="20" class="svg-ink" font-family="Poppins" font-size="14" font-weight="600" text-anchor="middle">Open Source</text>
							<text x="370" y="36" class="svg-small svg-faint" text-anchor="middle">WooCommerce, Shopware</text>
							<text x="580" y="20" class="svg-ink" font-family="Poppins" font-size="14" font-weight="600" text-anchor="middle">Eigenbau</text>
							<text x="580" y="36" class="svg-small svg-faint" text-anchor="middle">selbst programmiert</text>

							<g class="svg-small svg-soft">
								<text x="4" y="72">Startkosten</text>
								<text x="4" y="112">Zeit bis online</text>
								<text x="4" y="152">Flexibilität</text>
								<text x="4" y="192">Technikwissen nötig</text>
							</g>

							<!-- Balken: 1 = wenig, 5 = viel -->
							<g>
								<rect x="96" y="60" width="30" height="16" rx="4" fill="var(--accent)"></rect>
								<rect x="130" y="60" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="164" y="60" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="286" y="60" width="30" height="16" rx="4" fill="var(--accent)"></rect>
								<rect x="320" y="60" width="30" height="16" rx="4" fill="var(--accent)"></rect>
								<rect x="354" y="60" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="510" y="60" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="544" y="60" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="578" y="60" width="30" height="16" rx="4" fill="var(--warn)"></rect>

								<rect x="96" y="100" width="30" height="16" rx="4" fill="var(--accent)"></rect>
								<rect x="130" y="100" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="164" y="100" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="286" y="100" width="30" height="16" rx="4" fill="var(--accent)"></rect>
								<rect x="320" y="100" width="30" height="16" rx="4" fill="var(--accent)"></rect>
								<rect x="354" y="100" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="510" y="100" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="544" y="100" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="578" y="100" width="30" height="16" rx="4" fill="var(--warn)"></rect>

								<rect x="96" y="140" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="130" y="140" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="164" y="140" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="286" y="140" width="30" height="16" rx="4" fill="var(--ok)"></rect>
								<rect x="320" y="140" width="30" height="16" rx="4" fill="var(--ok)"></rect>
								<rect x="354" y="140" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="510" y="140" width="30" height="16" rx="4" fill="var(--ok)"></rect>
								<rect x="544" y="140" width="30" height="16" rx="4" fill="var(--ok)"></rect>
								<rect x="578" y="140" width="30" height="16" rx="4" fill="var(--ok)"></rect>

								<rect x="96" y="180" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="130" y="180" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="164" y="180" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="286" y="180" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="320" y="180" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="354" y="180" width="30" height="16" rx="4" fill="var(--line)"></rect>
								<rect x="510" y="180" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="544" y="180" width="30" height="16" rx="4" fill="var(--warn)"></rect>
								<rect x="578" y="180" width="30" height="16" rx="4" fill="var(--warn)"></rect>
							</g>
							<text x="4" y="220" class="svg-small svg-faint">■ wenig ·  ■■ mittel ·  ■■■ viel</text>
							<text x="330" y="220" class="svg-small svg-faint">Für die meisten Junior Companies ist der Mietshop richtig.</text>
						</g>
					</svg>
				</div>
				<figcaption>Ein Schuljahr ist kurz. Was ihr an Zeit in die Technik steckt, fehlt beim Verkaufen.</figcaption>
			</figure>

			<div class="note">
				<span class="ico i-tipp">TIPP</span>
				<span>Prüft vor der Entscheidung drei Dinge: Gibt es die Oberfläche auf Deutsch?
				Sind die österreichischen Zahlungsarten eingebaut? Und was kostet es <b>pro Monat</b>,
				nicht nur im Aktionszeitraum? Ein Marktplatz wie Etsy oder ein Verkauf über Instagram
				kann für den Start auch reichen — schreibt dann aber ins Konzept, warum.</span>
			</div>

			<h3>Die Sitemap: welche Seiten braucht ihr?</h3>
			<p>Die Sitemap ist der Bauplan eurer Website. Sie zeigt, welche Seiten es gibt und wie
			man von einer zur anderen kommt. Faustregel: <b>Jede Seite ist von der Startseite aus in
			höchstens drei Klicks erreichbar.</b></p>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 270" role="img" aria-label="Sitemap eines Junior-Company-Shops mit Startseite, Shop, Kategorien, Produktseite, Über uns, Kontakt und Pflichtseiten">
						<g class="svg-label">
							<rect x="278" y="8" width="140" height="34" rx="8" fill="var(--accent)"></rect>
							<text x="348" y="30" text-anchor="middle" fill="#fff" font-weight="600">Startseite</text>

							<g stroke="var(--line)" stroke-width="1.6" fill="none">
								<path d="M348 42 L348 58"></path>
								<path d="M100 58 L596 58"></path>
								<path d="M100 58 L100 74"></path>
								<path d="M266 58 L266 74"></path>
								<path d="M430 58 L430 74"></path>
								<path d="M596 58 L596 74"></path>
							</g>

							<rect x="34" y="74" width="132" height="30" rx="7" fill="var(--wash-strong)"></rect>
							<text x="100" y="94" text-anchor="middle" class="svg-ink" font-weight="600">Shop</text>
							<rect x="200" y="74" width="132" height="30" rx="7" fill="var(--surface-2)"></rect>
							<text x="266" y="94" text-anchor="middle" class="svg-ink" font-weight="600">Über uns</text>
							<rect x="364" y="74" width="132" height="30" rx="7" fill="var(--surface-2)"></rect>
							<text x="430" y="94" text-anchor="middle" class="svg-ink" font-weight="600">Blog</text>
							<rect x="530" y="74" width="132" height="30" rx="7" fill="var(--surface-2)"></rect>
							<text x="596" y="94" text-anchor="middle" class="svg-ink" font-weight="600">Kontakt</text>

							<g stroke="var(--line)" stroke-width="1.6" fill="none">
								<path d="M100 104 L100 120"></path>
								<path d="M46 120 L154 120"></path>
								<path d="M46 120 L46 136"></path>
								<path d="M154 120 L154 136"></path>
								<path d="M100 166 L100 182"></path>
							</g>
							<rect x="4" y="136" width="84" height="28" rx="7" fill="var(--wash)"></rect>
							<text x="46" y="155" text-anchor="middle" class="svg-small svg-ink">Kategorie A</text>
							<rect x="112" y="136" width="84" height="28" rx="7" fill="var(--wash)"></rect>
							<text x="154" y="155" text-anchor="middle" class="svg-small svg-ink">Kategorie B</text>
							<rect x="34" y="182" width="132" height="28" rx="7" fill="var(--wash)"></rect>
							<text x="100" y="201" text-anchor="middle" class="svg-small svg-ink">Produktseite</text>

							<g stroke="var(--line)" stroke-width="1.6" fill="none" stroke-dasharray="3 3">
								<path d="M266 104 L266 136"></path>
							</g>
							<rect x="200" y="136" width="132" height="28" rx="7" fill="var(--surface-2)"></rect>
							<text x="266" y="155" text-anchor="middle" class="svg-small svg-ink">Unser Team</text>

							<line x1="4" y1="228" x2="696" y2="228" stroke="var(--line)" stroke-width="1"></line>
							<text x="4" y="248" class="svg-small svg-faint" letter-spacing="1">IM FUSSBEREICH — AUF JEDER SEITE ERREICHBAR</text>
							<g class="svg-small">
								<rect x="4" y="254" width="80" height="12" rx="3" fill="var(--warn)" opacity=".18"></rect>
								<text x="8" y="264" fill="var(--warn)">Impressum</text>
								<rect x="92" y="254" width="94" height="12" rx="3" fill="var(--warn)" opacity=".18"></rect>
								<text x="96" y="264" fill="var(--warn)">Datenschutz</text>
								<rect x="194" y="254" width="48" height="12" rx="3" fill="var(--warn)" opacity=".18"></rect>
								<text x="198" y="264" fill="var(--warn)">AGB</text>
								<rect x="250" y="254" width="120" height="12" rx="3" fill="var(--warn)" opacity=".18"></rect>
								<text x="254" y="264" fill="var(--warn)">Widerrufsbelehrung</text>
								<rect x="378" y="254" width="112" height="12" rx="3" fill="var(--line)"></rect>
								<text x="382" y="264" class="svg-soft">Versand &amp; Zahlung</text>
								<rect x="498" y="254" width="46" height="12" rx="3" fill="var(--line)"></rect>
								<text x="502" y="264" class="svg-soft">FAQ</text>
							</g>
						</g>
					</svg>
				</div>
				<figcaption>Die orangen Seiten im Fußbereich sind aus Kapitel 3 verpflichtend — sie gehören von jeder Seite aus erreichbar.</figcaption>
			</figure>

			<h3>Technik, die man merkt</h3>
			<div class="grid two">
				<div class="card">
					<div class="karten-kopf">${ico('stoppuhr')}<h4>Ladezeit</h4></div>
					<p>Jede zusätzliche Sekunde kostet Bestellungen. Größter Hebel bei Schülerprojekten:
					Produktfotos verkleinern. Ein 4-MB-Handyfoto gehört auf unter 300 KB.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('handy')}<h4>Mobil zuerst</h4></div>
					<p>Der Großteil eurer Zielgruppe kauft am Handy. Testet jede Seite am Telefon,
					nicht nur am Schulrechner — vor allem Warenkorb und Kassa.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('schloss')}<h4>HTTPS</h4></div>
					<p>Ohne gültiges Zertifikat warnt der Browser vor eurer Seite. Bei Mietshops ist
					es eingebaut, sonst über den Hoster einrichten.</p>
				</div>
				<div class="card">
					<div class="karten-kopf">${ico('schild')}<h4>Sicherung</h4></div>
					<p>Regelmäßige Backups und getrennte Zugänge je Person. Ein gemeinsames Passwort
					im Klassenchat ist keine Zugriffsverwaltung.</p>
				</div>
			</div>

			<h3>Was auf eine gute Produktseite gehört</h3>
			<ul class="bullets">
				<li><b>Mehrere Fotos</b> aus verschiedenen Winkeln, eines mit Größenvergleich in der Hand.</li>
				<li><b>Beschreibung, die Fragen beantwortet:</b> Maße, Material, Pflege, Lieferzeit.</li>
				<li><b>Preis inklusive Steuer</b>, Versandkosten sichtbar oder verlinkt.</li>
				<li><b>Verfügbarkeit</b> — ehrlich, nicht künstlich knapp.</li>
				<li><b>Bewertungen</b>, sobald es echte gibt.</li>
			</ul>`
	},
	{
		no: 6,
		title: 'Bezahlen, sicher',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [6],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">06</span>
					<div>
							<h2>Bezahlen, sicher</h2>
							<p class="chapter-sub">Zahlungsarten, Zahlungsdienstleister und was bei der Sicherheit zählt.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[6].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[6].webp}" alt="Zwei Jugendliche bezahlen mit Karte am Smartphone, daneben Schloss und Häkchen" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>die fünf Schritte einer Onlinezahlung erklären</li>
					<li>Zahlungsarten nach Kosten, Risiko und Beliebtheit auswählen</li>
					<li>begründen, warum ihr Kartendaten niemals selbst speichert</li>
				</ul>
			</div>

			<h3>Was ein Zahlungsdienstleister macht</h3>
			<p>Ein Payment Gateway ist der Vermittler zwischen eurem Shop und dem Bankensystem. Er
			verschlüsselt die Daten, holt die Freigabe der Bank ein und meldet dem Shop zurück, ob
			das Geld unterwegs ist. Ihr seht die Kartennummer dabei nie — und das ist der Punkt.</p>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 116" role="img" aria-label="Fünf Schritte einer Onlinezahlung vom Checkout bis zur Gutschrift">
						<g class="svg-label">
							<g>
								<rect x="2" y="40" width="122" height="62" rx="10" fill="var(--wash)"></rect>
								<text x="63" y="66" text-anchor="middle" class="svg-ink" font-weight="600">Kassa</text>
								<text x="63" y="84" text-anchor="middle" class="svg-small svg-soft">Zahlungsart wählen</text>
								<circle cx="14" cy="52" r="9" fill="var(--accent)"></circle>
								<text x="14" y="56" text-anchor="middle" fill="#fff" font-size="10" font-weight="700">1</text>
							</g>
							<g>
								<rect x="142" y="40" width="122" height="62" rx="10" fill="var(--wash)"></rect>
								<text x="203" y="66" text-anchor="middle" class="svg-ink" font-weight="600">Verschlüsselt</text>
								<text x="203" y="84" text-anchor="middle" class="svg-small svg-soft">Gateway übermittelt</text>
								<circle cx="154" cy="52" r="9" fill="var(--accent)"></circle>
								<text x="154" y="56" text-anchor="middle" fill="#fff" font-size="10" font-weight="700">2</text>
							</g>
							<g>
								<rect x="282" y="40" width="122" height="62" rx="10" fill="var(--wash)"></rect>
								<text x="343" y="66" text-anchor="middle" class="svg-ink" font-weight="600">Freigabe</text>
								<text x="343" y="84" text-anchor="middle" class="svg-small svg-soft">Bank prüft, 3-D Secure</text>
								<circle cx="294" cy="52" r="9" fill="var(--accent)"></circle>
								<text x="294" y="56" text-anchor="middle" fill="#fff" font-size="10" font-weight="700">3</text>
							</g>
							<g>
								<rect x="422" y="40" width="122" height="62" rx="10" fill="var(--wash)"></rect>
								<text x="483" y="66" text-anchor="middle" class="svg-ink" font-weight="600">Bestätigung</text>
								<text x="483" y="84" text-anchor="middle" class="svg-small svg-soft">Shop zeigt Danke-Seite</text>
								<circle cx="434" cy="52" r="9" fill="var(--accent)"></circle>
								<text x="434" y="56" text-anchor="middle" fill="#fff" font-size="10" font-weight="700">4</text>
							</g>
							<g>
								<rect x="562" y="40" width="134" height="62" rx="10" fill="var(--ok-wash)"></rect>
								<text x="629" y="66" text-anchor="middle" class="svg-ink" font-weight="600">Gutschrift</text>
								<text x="629" y="84" text-anchor="middle" class="svg-small svg-soft">Geld am Konto</text>
								<circle cx="574" cy="52" r="9" fill="var(--ok)"></circle>
								<text x="574" y="56" text-anchor="middle" fill="#fff" font-size="10" font-weight="700">5</text>
							</g>

							<g stroke="var(--ink-faint)" stroke-width="1.6" fill="none">
								<path d="M126 71 L138 71" marker-end="url(#ar6)"></path>
								<path d="M266 71 L278 71" marker-end="url(#ar6)"></path>
								<path d="M406 71 L418 71" marker-end="url(#ar6)"></path>
								<path d="M546 71 L558 71" marker-end="url(#ar6)"></path>
							</g>
							<defs>
								<marker id="ar6" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
									<path d="M0 0 L8 4 L0 8 z" fill="var(--ink-faint)"></path>
								</marker>
							</defs>

							<text x="2" y="24" class="svg-small svg-faint" letter-spacing="1">SEKUNDEN 0 – 3</text>
							<text x="528" y="24" class="svg-small svg-faint" letter-spacing="1">1 – 3 WERKTAGE SPÄTER</text>
						</g>
					</svg>
				</div>
				<figcaption>Zwischen Schritt 4 und 5 liegen ein bis drei Werktage. „Bezahlt" auf der Danke-Seite heißt noch nicht „Geld am Konto".</figcaption>
			</figure>

			<h3>Welche Zahlungsarten für eine Junior Company?</h3>
			<div class="tablewrap">
				<table>
					<thead><tr><th>Zahlungsart</th><th>Passt, weil</th><th>Achtung</th></tr></thead>
					<tbody>
						<tr><td><b>Barzahlung bei Abholung</b></td><td>Keine Gebühren, kein Zahlungsausfall, persönlicher Kontakt in der Schule</td><td>Nur regional; Wechselgeld und Beleg nicht vergessen</td></tr>
						<tr><td><b>Sofortüberweisung / eps</b></td><td>In Österreich verbreitet, Geld ist sofort zugesagt</td><td>Für ältere Zielgruppen vertrauter als für 15-Jährige</td></tr>
						<tr><td><b>PayPal</b></td><td>Hohe Bekanntheit, senkt die Hemmschwelle spürbar</td><td>Gebühren je Transaktion; Konto muss auf die Company laufen</td></tr>
						<tr><td><b>Kredit-/Debitkarte</b></td><td>Funktioniert international, gewohnt aus anderen Shops</td><td>Braucht 3-D Secure; Vertrag über den Zahlungsdienstleister</td></tr>
						<tr><td><b>Klarna &amp; Kauf auf Rechnung</b></td><td>Sehr beliebt bei jungen Käufer:innen, erhöht die Conversion</td><td>Höhere Gebühren, Mahnwesen; Alter der Käufer:innen beachten</td></tr>
						<tr><td><b>Vorauskasse per Überweisung</b></td><td>Kostenlos, kein Ausfallrisiko</td><td>Bremst massiv — viele brechen ab, wenn das die einzige Option ist</td></tr>
					</tbody>
				</table>
			</div>

			<div class="note">
				<span class="ico i-merke">MERKE</span>
				<span>Zwei bis vier Zahlungsarten sind richtig: eine gewohnte (PayPal oder Karte),
				eine österreichische (eps), eine ohne Gebühren (Abholung/Überweisung). Zu wenig
				Auswahl kostet Bestellungen, zu viel Auswahl verwirrt an der Kassa.</span>
			</div>

			<h3>Sicherheit — vier Regeln</h3>
			<ol class="steps">
				<li><b>Niemals selbst speichern.</b> Kartennummern, Prüfziffern und Bankzugangsdaten haben in eurer Tabelle nichts verloren — dafür gibt es den Zahlungsdienstleister. Nehmt sie auch nicht per E-Mail, Chat oder Zettel entgegen.</li>
				<li><b>3-D Secure aktiviert lassen.</b> Die zusätzliche Bestätigung in der Banking-App ist Pflicht und verschiebt das Betrugsrisiko weg von euch.</li>
				<li><b>HTTPS überall.</b> Nicht nur auf der Kassaseite, sondern auf der ganzen Website.</li>
				<li><b>Zugänge trennen.</b> Jede Person im Team hat ein eigenes Konto im Shop-Backend, und wer die Company verlässt, verliert den Zugang.</li>
			</ol>`
	},
	{
		no: 7,
		title: 'Verpacken, verschicken, zurücknehmen',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [7],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">07</span>
					<div>
							<h2>Verpacken, verschicken, zurücknehmen</h2>
							<p class="chapter-sub">Auftragsabwicklung, Versand, Retouren und Kundenservice.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[7].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[7].webp}" alt="Drei Jugendliche verpacken Bestellungen, darüber ein Rückgabepfeil" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>den Weg einer Bestellung von der Kassa bis zum Briefkasten beschreiben</li>
					<li>einen kundenfreundlichen Retourenprozess entwerfen</li>
					<li>die drei Kundenservice-Kanäle nach Aufwand und Wirkung einordnen</li>
				</ul>
			</div>

			<h3>Der Weg einer Bestellung</h3>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 250" role="img" aria-label="Ablauf einer Bestellung mit Retourenschleife zurück ins Lager">
						<g class="svg-label">
							<g>
								<rect x="4" y="30" width="104" height="52" rx="9" fill="var(--wash)"></rect>
								<text x="56" y="52" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Bestellung</text>
								<text x="56" y="68" text-anchor="middle" class="svg-small svg-soft">eingegangen</text>
							</g>
							<g>
								<rect x="124" y="30" width="104" height="52" rx="9" fill="var(--wash)"></rect>
								<text x="176" y="52" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Kommissionieren</text>
								<text x="176" y="68" text-anchor="middle" class="svg-small svg-soft">Ware holen</text>
							</g>
							<g>
								<rect x="244" y="30" width="104" height="52" rx="9" fill="var(--wash)"></rect>
								<text x="296" y="52" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Verpacken</text>
								<text x="296" y="68" text-anchor="middle" class="svg-small svg-soft">+ Beleg beilegen</text>
							</g>
							<g>
								<rect x="364" y="30" width="104" height="52" rx="9" fill="var(--wash)"></rect>
								<text x="416" y="52" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Etikett</text>
								<text x="416" y="68" text-anchor="middle" class="svg-small svg-soft">+ Übergabe</text>
							</g>
							<g>
								<rect x="484" y="30" width="104" height="52" rx="9" fill="var(--wash)"></rect>
								<text x="536" y="52" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Sendungsnummer</text>
								<text x="536" y="68" text-anchor="middle" class="svg-small svg-soft">mailen</text>
							</g>
							<g>
								<rect x="604" y="30" width="92" height="52" rx="9" fill="var(--ok-wash)"></rect>
								<text x="650" y="52" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Zugestellt</text>
								<text x="650" y="68" text-anchor="middle" class="svg-small svg-soft">1 – 3 Tage</text>
							</g>

							<g stroke="var(--ink-faint)" stroke-width="1.5" fill="none">
								<path d="M110 56 L120 56" marker-end="url(#ar7)"></path>
								<path d="M230 56 L240 56" marker-end="url(#ar7)"></path>
								<path d="M350 56 L360 56" marker-end="url(#ar7)"></path>
								<path d="M470 56 L480 56" marker-end="url(#ar7)"></path>
								<path d="M590 56 L600 56" marker-end="url(#ar7)"></path>
							</g>
							<defs>
								<marker id="ar7" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
									<path d="M0 0 L8 4 L0 8 z" fill="var(--ink-faint)"></path>
								</marker>
							</defs>

							<!-- Retourenschleife -->
							<path d="M650 86 L650 126 Q650 138 638 138 L220 138 Q208 138 208 150 L208 162"
								stroke="var(--warn)" stroke-width="1.8" fill="none" stroke-dasharray="5 4" marker-end="url(#ar7w)"></path>
							<defs>
								<marker id="ar7w" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
									<path d="M0 0 L8 4 L0 8 z" fill="var(--warn)"></path>
								</marker>
							</defs>
							<text x="360" y="132" class="svg-small" fill="var(--warn)" font-weight="600">Retoure — 14 Tage Rücktrittsrecht</text>

							<g>
								<rect x="124" y="166" width="168" height="52" rx="9" fill="var(--warn-wash)"></rect>
								<text x="208" y="188" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Prüfen</text>
								<text x="208" y="204" text-anchor="middle" class="svg-small svg-soft">Zustand + Grund</text>
							</g>
							<g>
								<rect x="312" y="166" width="168" height="52" rx="9" fill="var(--warn-wash)"></rect>
								<text x="396" y="188" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Geld zurück</text>
								<text x="396" y="204" text-anchor="middle" class="svg-small svg-soft">binnen 14 Tagen</text>
							</g>
							<g>
								<rect x="500" y="166" width="168" height="52" rx="9" fill="var(--warn-wash)"></rect>
								<text x="584" y="188" text-anchor="middle" class="svg-small svg-ink" font-weight="600">Wieder</text>
								<text x="584" y="204" text-anchor="middle" class="svg-small svg-soft">einlagern</text>
							</g>
							<g stroke="var(--warn)" stroke-width="1.5" fill="none">
								<path d="M294 192 L308 192" marker-end="url(#ar7w)"></path>
								<path d="M482 192 L496 192" marker-end="url(#ar7w)"></path>
							</g>
						</g>
					</svg>
				</div>
				<figcaption>Die Rückerstattung muss binnen 14 Tagen ab Rücktrittserklärung erfolgen — auch die ursprünglichen Standard-Versandkosten.</figcaption>
			</figure>

			<h3>Versand planen</h3>
			<ul class="bullets">
				<li><b>Zusteller wählen:</b> Österreichische Post, GLS, DPD oder DHL. Vergleicht Preis pro Paket, Abholung vs. Selbstaufgabe und ob es eine Abgabestelle in Schulnähe gibt.</li>
				<li><b>Versandkosten entscheiden:</b> pauschal (einfach zu verstehen), gestaffelt nach Gewicht (gerechter) oder gratis ab einem Bestellwert (erhöht den Warenkorbwert).</li>
				<li><b>Verpackung ist Marketing:</b> Ein Karton in passender Größe, Füllmaterial aus Papier statt Plastik, ein handgeschriebener Zettel. Das kostet Cent und bringt Fotos in den sozialen Medien.</li>
				<li><b>Abholung in der Schule</b> ist euer Vorteil gegenüber jedem Onlineriesen: kein Porto, persönlicher Kontakt, sofortige Übergabe.</li>
			</ul>

			<h3>Fulfillment: alles selbst oder auslagern?</h3>
			<p>Fulfillment heißt: Lagern, Verpacken, Versenden und Retouren übernimmt ein
			Dienstleister. Für Junior Companies lohnt sich das praktisch nie — die Mengen sind zu
			klein und die Fixkosten zu hoch. Der Begriff gehört trotzdem ins Konzept, weil er zeigt,
			dass ihr wisst, wie das ab einer gewissen Größe gelöst wird.</p>

			<h3>Kundenservice: drei Kanäle</h3>
			<div class="grid three">
				<div class="card">
					<span class="tag">Persönlich</span>
					<div class="karten-kopf">${ico('telefon')}<h4>Telefon</h4></div>
					<p>Löst komplizierte Fälle am schnellsten und wirkt am verbindlichsten.</p>
					<ul><li>Zeitaufwendig</li><li>Braucht feste Zeiten — sonst besser nicht anbieten</li></ul>
				</div>
				<div class="card">
					<span class="tag">Dokumentiert</span>
					<div class="karten-kopf">${ico('brief')}<h4>E-Mail</h4></div>
					<p>Alles ist nachlesbar, Textbausteine sparen Zeit, jede:r im Team kann übernehmen.</p>
					<ul><li>Langsamer</li><li>Antwortzeit zusagen und einhalten (z. B. 24 h werktags)</li></ul>
				</div>
				<div class="card">
					<span class="tag">Sofort</span>
					<div class="karten-kopf">${ico('sprechblase')}<h4>Chat / Chatbot</h4></div>
					<p>Direkte Antwort im Shop, Standardfragen kann ein Bot übernehmen.</p>
					<ul><li>Ein toter Chat ist schlimmer als keiner</li><li>Bot muss an einen Menschen übergeben können</li></ul>
				</div>
			</div>

			<div class="note warnN">
				<span class="ico i-warn">ACHTUNG</span>
				<span>Versprecht nur, was ihr in der Schulwoche auch haltet. „Antwort binnen 24 Stunden"
				gilt auch am Freitagnachmittag. Lieber „werktags binnen zwei Tagen" schreiben und
				zuverlässig sein.</span>
			</div>

			${FALL_ZALANDO}`
	},
	{
		no: 8,
		title: 'Gefunden werden',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [8],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">08</span>
					<div>
							<h2>Gefunden werden</h2>
							<p class="chapter-sub">Wie Suchmaschinen arbeiten und was ihr dafür tun könnt.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[8].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[8].webp}" alt="Zwei Jugendliche staunen über eine Lupe vor einem Suchfeld mit Ergebnisbalken" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>die drei Arbeitsschritte einer Suchmaschine benennen</li>
					<li>fünf Keywords für eure Junior Company finden und begründen</li>
					<li>On-Page- von Off-Page-Maßnahmen unterscheiden</li>
				</ul>
			</div>

			<h3>Crawlen, indexieren, ranken</h3>
			<p>Eine Suchmaschine tut drei Dinge. Ein Programm — der Crawler — folgt Links und liest
			Seiten. Der Index speichert das Gelesene geordnet ab. Und der Ranking-Algorithmus
			entscheidet bei jeder Suchanfrage, welche der gespeicherten Seiten oben steht.</p>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 250" role="img" aria-label="Crawling, Indexierung und Ranking sowie der Aufbau eines Suchergebnisses">
						<g class="svg-label">
							<g>
								<rect x="4" y="26" width="150" height="72" rx="10" fill="var(--wash)"></rect>
								<text x="79" y="52" text-anchor="middle" class="svg-ink" font-weight="600">1 · Crawling</text>
								<text x="79" y="70" text-anchor="middle" class="svg-small svg-soft">Bots folgen Links</text>
								<text x="79" y="86" text-anchor="middle" class="svg-small svg-soft">und lesen Seiten</text>
							</g>
							<g>
								<rect x="174" y="26" width="150" height="72" rx="10" fill="var(--wash)"></rect>
								<text x="249" y="52" text-anchor="middle" class="svg-ink" font-weight="600">2 · Index</text>
								<text x="249" y="70" text-anchor="middle" class="svg-small svg-soft">Inhalte werden</text>
								<text x="249" y="86" text-anchor="middle" class="svg-small svg-soft">geordnet gespeichert</text>
							</g>
							<g>
								<rect x="344" y="26" width="150" height="72" rx="10" fill="var(--accent)"></rect>
								<text x="419" y="52" text-anchor="middle" fill="#fff" font-weight="600">3 · Ranking</text>
								<text x="419" y="70" text-anchor="middle" class="svg-small" fill="#fff" opacity=".85">Relevanz · Autorität</text>
								<text x="419" y="86" text-anchor="middle" class="svg-small" fill="#fff" opacity=".85">Nutzersignale</text>
							</g>
							<g stroke="var(--ink-faint)" stroke-width="1.6" fill="none">
								<path d="M156 62 L170 62" marker-end="url(#ar8)"></path>
								<path d="M326 62 L340 62" marker-end="url(#ar8)"></path>
							</g>
							<defs>
								<marker id="ar8" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
									<path d="M0 0 L8 4 L0 8 z" fill="var(--ink-faint)"></path>
								</marker>
							</defs>
							<text x="514" y="46" class="svg-small svg-faint" letter-spacing="1">SO SIEHT DAS ERGEBNIS AUS</text>
							<rect x="514" y="56" width="182" height="60" rx="8" fill="var(--surface-2)"></rect>
							<text x="526" y="74" class="svg-small" fill="var(--accent)" font-weight="600">Notizbücher aus Graz …</text>
							<text x="526" y="88" class="svg-small svg-faint">notizbuch-hak-graz.at › shop</text>
							<text x="526" y="104" class="svg-small svg-soft">Fadengeheftet, aus Pappe …</text>

							<line x1="4" y1="132" x2="696" y2="132" stroke="var(--line)"></line>

							<text x="4" y="158" class="svg-small svg-faint" letter-spacing="1">ON-PAGE — AUF EURER SEITE</text>
							<g class="svg-small svg-soft">
								<text x="4" y="180">Seitentitel mit dem Keyword</text>
								<text x="4" y="198">Zwischenüberschriften (H1, H2)</text>
								<text x="4" y="216">Meta-Beschreibung, die zum Klick einlädt</text>
								<text x="4" y="234">Bild-Alternativtexte, sprechende Adressen</text>
							</g>
							<text x="360" y="158" class="svg-small svg-faint" letter-spacing="1">OFF-PAGE — AUSSERHALB</text>
							<g class="svg-small svg-soft">
								<text x="360" y="180">Links von der Schulwebsite</text>
								<text x="360" y="198">Eintrag bei JA Austria, Wirtschaftskammer</text>
								<text x="360" y="216">Berichte in Lokalzeitung und Schulblog</text>
								<text x="360" y="234">Erwähnungen in sozialen Medien</text>
							</g>
						</g>
					</svg>
				</div>
				<figcaption>Relevanz kommt von eurem Text, Autorität von Links anderer Seiten. Als Schulprojekt habt ihr beim zweiten Punkt einen Vorteil: Schule, Gemeinde und Presse verlinken gerne.</figcaption>
			</figure>

			<h3>Keywords finden</h3>
			<p>Ein Keyword ist der Suchbegriff, den eure Zielgruppe wirklich eintippt — nicht der,
			den ihr für richtig haltet. „Fadengeheftetes Notizbuch" sucht niemand; „Notizbuch
			selbst gestalten" schon.</p>
			<ol class="steps">
				<li><b>Sammeln.</b> Wie würdet ihr selbst suchen? Fragt fünf Leute aus der Zielgruppe.</li>
				<li><b>Vorschläge nutzen.</b> Tippt den Begriff bei Google ein und schaut auf die automatischen Ergänzungen und auf „Ähnliche Fragen".</li>
				<li><b>Längere Begriffe bevorzugen.</b> Bei „Notizbuch" habt ihr keine Chance. Bei „Notizbuch mit Namen Graz" schon.</li>
				<li><b>Zuordnen.</b> Jedes Keyword bekommt genau eine Seite. Zwei Seiten zum selben Begriff machen sich gegenseitig Konkurrenz.</li>
				<li><b>Einbauen.</b> In Titel, Überschrift, ersten Absatz und Bildtext — natürlich formuliert, nicht gestapelt.</li>
			</ol>

			<div class="note warnN">
				<span class="ico i-warn">TYPISCHE FEHLER</span>
				<span><b>Keyword-Stuffing</b> („Notizbuch kaufen Notizbuch günstig Notizbuch Graz") wird
				abgewertet, nicht belohnt. <b>Gekaufte Links</b> ebenso. Und wer die mobile Ansicht
				oder die Ladezeit ignoriert, verliert Positionen, egal wie gut der Text ist.</span>
			</div>

			<h3>Wie lange dauert das?</h3>
			<p>SEO wirkt langsam — Wochen bis Monate. Für ein Schuljahr heißt das: Fangt in den
			ersten Wochen an, und rechnet nicht damit, dass Suchmaschinen euren Weihnachtsverkauf
			retten. Für schnelle Sichtbarkeit sind bezahlte Anzeigen und soziale Medien da
			(Kapitel 9).</p>`
	},
	{
		no: 9,
		title: 'Der Marketing-Mix',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [9, 10, 11, 12],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">09</span>
					<div>
							<h2>Der Marketing-Mix</h2>
							<p class="chapter-sub">Content, Social Media, bezahlte Anzeigen und E-Mail — und wann welches Mittel wirkt.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[9].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[9].webp}" alt="Drei Jugendliche filmen ein Produkt mit Smartphone und Ringlicht" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>den Kauftrichter in drei Phasen einteilen und jeder Phase Inhalte zuordnen</li>
					<li>eine Plattform anhand eurer Persona auswählen statt nach Gefühl</li>
					<li>eine kleine Anzeigenkampagne und eine Willkommens-E-Mail planen</li>
				</ul>
			</div>

			<h3>TOFU, MOFU, BOFU: derselbe Mensch, drei Phasen</h3>
			<p>Niemand kauft beim ersten Kontakt. Der Weg führt von „kennt euch nicht" über
			„überlegt" zu „kauft". Für jede Phase braucht es andere Inhalte — der häufigste Fehler
			im Schülermarketing ist, allen Menschen immer nur „Jetzt kaufen!" zu zeigen.</p>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 290" role="img" aria-label="Marketing-Trichter mit den Phasen TOFU, MOFU und BOFU samt Zielen, Mitteln und Kennzahlen">
						<g class="svg-label">
							<path d="M20 20 L400 20 L374 106 L46 106 Z" fill="var(--wash-strong)"></path>
							<path d="M50 112 L370 112 L344 198 L76 198 Z" fill="var(--wash)"></path>
							<path d="M80 204 L340 204 L316 280 L104 280 Z" fill="var(--accent-deep)"></path>

							<text x="210" y="52" text-anchor="middle" class="svg-ink" font-family="Poppins" font-size="15" font-weight="700">TOFU</text>
							<text x="210" y="72" text-anchor="middle" class="svg-small svg-soft">Aufmerksamkeit — „Wer seid ihr?"</text>
							<text x="210" y="92" text-anchor="middle" class="svg-small svg-soft">Reels, Blogtipps, Schulmesse, Presse</text>

							<text x="210" y="142" text-anchor="middle" class="svg-ink" font-family="Poppins" font-size="15" font-weight="700">MOFU</text>
							<text x="210" y="162" text-anchor="middle" class="svg-small svg-soft">Interesse — „Ist das was für mich?"</text>
							<text x="210" y="182" text-anchor="middle" class="svg-small svg-soft">Produktvideos, Vergleiche, Newsletter</text>

							<text x="210" y="230" text-anchor="middle" fill="#fff" font-family="Poppins" font-size="15" font-weight="700">BOFU</text>
							<text x="210" y="250" text-anchor="middle" class="svg-small" fill="#fff">Kauf — „Warum jetzt?"</text>
							<text x="210" y="270" text-anchor="middle" class="svg-small" fill="#fff">Gutschein, Gratisversand, Garantie</text>

							<line x1="430" y1="20" x2="430" y2="280" stroke="var(--line)"></line>

							<text x="452" y="34" class="svg-small svg-faint" letter-spacing="1">WORAN IHR DEN ERFOLG MESST</text>
							<g>
								<circle cx="460" cy="62" r="5" fill="var(--accent)"></circle>
								<text x="476" y="66" class="svg-soft">Reichweite, neue Follower, Besuche</text>
								<circle cx="460" cy="98" r="5" fill="var(--accent)"></circle>
								<text x="476" y="102" class="svg-soft">Verweildauer, Newsletter-Abos</text>
								<circle cx="460" cy="134" r="5" fill="var(--accent)"></circle>
								<text x="476" y="138" class="svg-soft">Conversion Rate, Umsatz je Kanal</text>
							</g>

							<text x="452" y="178" class="svg-small svg-faint" letter-spacing="1">TYPISCHER FEHLER</text>
							<text x="452" y="200" class="svg-small svg-soft">Nur BOFU-Inhalte posten. Wer euch noch</text>
							<text x="452" y="216" class="svg-small svg-soft">nicht kennt, wird von „Jetzt bestellen!"</text>
							<text x="452" y="232" class="svg-small svg-soft">nicht überzeugt, sondern übersieht es.</text>
							<text x="452" y="262" class="svg-small" fill="var(--accent-deep)" font-weight="600">Faustregel: 3 Beiträge, die etwas geben,</text>
							<text x="452" y="278" class="svg-small" fill="var(--accent-deep)" font-weight="600">auf 1 Beitrag, der etwas verkauft.</text>
						</g>
					</svg>
				</div>
				<figcaption>Derselbe Mensch braucht in jeder Phase etwas anderes. Ordnet jedem geplanten Beitrag vorher eine Phase zu — das allein hebt jede Social-Media-Planung.</figcaption>
			</figure>

			<h3>Content-Marketing: etwas geben, bevor man nimmt</h3>
			<p>Content-Marketing heißt, Inhalte zu veröffentlichen, die für sich genommen nützlich
			sind — auch für jemanden, der nichts kauft. Das baut Vertrauen auf und hilft nebenbei
			der Suchmaschine (Kapitel 8).</p>
			<div class="grid three">
				<div class="card"><h4>Anleitungen</h4><p>„So pflegst du eine Kerze richtig." Nützlich, teilbar, passt zum Produkt.</p></div>
				<div class="card"><h4>Blick hinter die Kulissen</h4><p>Die Werkstatt, das Team, ein misslungener Versuch. Bei Schulprojekten der stärkste Inhalt überhaupt.</p></div>
				<div class="card"><h4>Erklärvideos</h4><p>60 Sekunden, hochkant, mit Untertiteln — die meisten schauen ohne Ton.</p></div>
				<div class="card"><h4>Kundengeschichten</h4><p>Eine echte Käuferin zeigt, wofür sie das Produkt nutzt.</p></div>
				<div class="card"><h4>Lookbook / Katalog</h4><p>Alle Produkte in einem gestalteten PDF — auch gut für Messen und Elternabende.</p></div>
				<div class="card"><h4>Zahlen und Wissen</h4><p>„Was 200 Schüler:innen über Nachhaltigkeit sagen." Eigene Umfrage, eigene Grafik, Presseanlass.</p></div>
			</div>

			<h3>Social Media: lieber ein Kanal richtig</h3>
			<p>Wählt die Plattform nach eurer Persona aus Kapitel 2, nicht nach dem, was das Team
			selbst nutzt. Und wählt <b>einen</b> Hauptkanal: Zwei gut gepflegte Kanäle sind besser
			als vier verwaiste.</p>
			<div class="tablewrap">
				<table>
					<thead><tr><th>Plattform</th><th>Stark bei</th><th>Was dort funktioniert</th></tr></thead>
					<tbody>
						<tr><td><b>Instagram</b></td><td>Jugendliche &amp; junge Erwachsene, visuelle Produkte</td><td>Reels, Stories mit Umfragen, gepflegtes Profilraster, Link im Profil</td></tr>
						<tr><td><b>TikTok</b></td><td>Unter 25, Reichweite ohne Follower</td><td>Kurzvideos mit Trend-Ton, „Wie es gemacht wird", Humor</td></tr>
						<tr><td><b>WhatsApp-Kanal</b></td><td>Direkte Erreichbarkeit im Ort</td><td>Kurze Ankündigungen, Abholtermine, Restposten</td></tr>
						<tr><td><b>Facebook</b></td><td>Eltern, Lehrkräfte, Gemeinde, Regionalgruppen</td><td>Veranstaltungen, längere Beiträge, lokale Gruppen</td></tr>
						<tr><td><b>LinkedIn</b></td><td>Firmen, Sponsoren, Wirtschaftsexpert:innen</td><td>Projektberichte, Meilensteine, Danksagungen an Partner</td></tr>
					</tbody>
				</table>
			</div>

			<h4>Die fünf Plattformen im Vergleich</h4>
			<p>Dieselben fünf, aber nach den vier Fragen sortiert, die für die Auswahl zählen.
			<b>Aufwand</b> heißt: Wie viel Zeit kostet ein Beitrag, der etwas bringt.</p>

			<div class="tablewrap">
				<table>
					<thead>
						<tr>
							<th>Plattform</th>
							<th>Reichweite ohne Follower</th>
							<th>Wer dort ist</th>
							<th>Aufwand je Beitrag</th>
							<th>Geeignet für</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td><b>TikTok</b></td>
							<td><b>hoch</b> — ein gutes Video läuft auch ohne Publikum</td>
							<td>13–25 Jahre</td>
							<td>mittel — Video schneiden, aber Handykamera reicht</td>
							<td>Produkte, die man <em>zeigen</em> kann; Blick hinter die Kulissen</td>
						</tr>
						<tr>
							<td><b>Instagram</b></td>
							<td>mittel — Reels ja, Bilder kaum</td>
							<td>16–40 Jahre</td>
							<td>mittel — gute Fotos brauchen Licht und Zeit</td>
							<td>Aussehen, Stimmung, wiedererkennbarer Auftritt</td>
						</tr>
						<tr>
							<td><b>WhatsApp-Kanal</b></td>
							<td><b>keine</b> — nur wer beitritt</td>
							<td>alle Altersgruppen im Ort</td>
							<td><b>niedrig</b> — drei Zeilen und ein Foto</td>
							<td>Abholtermine, Restposten, Stammkundschaft</td>
						</tr>
						<tr>
							<td><b>Facebook</b></td>
							<td>gering — außer in lokalen Gruppen</td>
							<td>ab 35 Jahren, Eltern und Gemeinde</td>
							<td>niedrig — Text genügt</td>
							<td>Veranstaltungen, Schulmesse, regionale Gruppen</td>
						</tr>
						<tr>
							<td><b>LinkedIn</b></td>
							<td>gering</td>
							<td>Berufstätige, Firmen</td>
							<td>mittel — der Ton ist ein anderer</td>
							<td>Sponsoren, Wirtschaftsexpert:innen, Projektberichte</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div class="note">
				<span class="ico i-wahl">SO WÄHLT IHR</span>
				<span>Geht die Spalten von links nach rechts durch: <b>Reichweite</b> zählt am
				Anfang am meisten, weil ihr noch niemanden habt. <b>Wer dort ist</b> muss zu eurer
				Persona aus Kapitel 2 passen — sonst nützt die Reichweite nichts. <b>Aufwand</b>
				entscheidet, ob ihr es im Schulalltag durchhaltet. Und die letzte Spalte sagt, ob
				euer Produkt dort überhaupt funktioniert. Bleiben zwei übrig, nehmt die mit dem
				geringeren Aufwand — der verlässliche zweite Kanal schlägt den ambitionierten
				ersten, der nach drei Wochen einschläft.</span>
			</div>

			${FALL_SHEIN_TIKTOK}

			<h3>Community statt Publikum</h3>
			<ul class="bullets">
				<li><b>Antwortet auf jeden Kommentar.</b> Sichtbare Antworten sind das billigste Vertrauenssignal, das es gibt.</li>
				<li><b>Fragt etwas.</b> Umfragen in Stories liefern euch nebenbei Daten für die Persona.</li>
				<li><b>Zeigt Menschen.</b> Beiträge mit Gesichtern schlagen Produktfotos auf weißem Grund.</li>
				<li><b>Regelmäßigkeit vor Menge.</b> Zwei Beiträge pro Woche, verlässlich, sind mehr wert als zehn in einer Woche und dann drei Wochen nichts.</li>
			</ul>

			<h3>Bezahlte Anzeigen: klein anfangen, genau messen</h3>
			<p>Mit Meta Ads oder Google Ads kauft ihr Reichweite. Das lohnt sich nur, wenn drei
			Dinge stimmen: eine klare Zielgruppe, ein konkretes Ziel und ein Budget, dessen Verlust
			ihr verkraftet. Für Junior Companies sind 30 bis 100 € über zwei Wochen ein realistischer
			Test.</p>
			<div class="grid two">
				<div class="card">
					<span class="tag">Google Ads</span>
					<h4>Nachfrage abholen</h4>
					<p>Ihr erscheint, wenn jemand aktiv sucht. Gut für BOFU: Die Kaufabsicht ist schon da.</p>
				</div>
				<div class="card">
					<span class="tag">Meta Ads</span>
					<h4>Nachfrage wecken</h4>
					<p>Ihr erscheint bei Menschen, die passen — auch wenn sie euch nicht suchen. Gut für TOFU und für Retargeting.</p>
				</div>
			</div>
			<p>Legt vorher fest, was ein Erfolg wäre: „50 € Budget, mindestens 15 Bestellungen,
			also höchstens 3,33 € Werbekosten pro Bestellung." Ohne diese Zahl könnt ihr hinterher
			nicht sagen, ob es funktioniert hat.</p>

			<h3>E-Mail: der Kanal, der euch gehört</h3>
			<p>Follower gehören der Plattform, E-Mail-Adressen gehören euch. Deshalb ist die
			Newsletter-Liste der wertvollste Marketingbesitz einer Junior Company.</p>
			<ol class="steps">
				<li><b>Anmeldung mit Gegenwert:</b> 10 % auf die erste Bestellung, eine Vorlage zum Download, früher Zugang zur neuen Serie.</li>
				<li><b>Einwilligung sauber einholen:</b> aktives Häkchen, kein vorausgefülltes Feld, Bestätigungsmail mit Link (Double Opt-in), Abmeldelink in jeder Aussendung.</li>
				<li><b>Willkommens-Sequenz:</b> Mail 1 sofort (danke, Gutscheincode), Mail 2 nach drei Tagen (eure Geschichte), Mail 3 nach einer Woche (Bestseller).</li>
				<li><b>Warenkorb-Erinnerung:</b> eine freundliche Mail nach 24 Stunden an alle, die abgebrochen haben — die wirksamste einzelne Automatisierung im Onlinehandel.</li>
				<li><b>Danach Takt halten:</b> alle zwei bis vier Wochen etwas Nützliches, nicht jede Woche ein Rabatt.</li>
			</ol>

			<div class="note">
				<span class="ico i-merke">MERKE</span>
				<span>Werbe-E-Mails ohne Einwilligung sind in Österreich unzulässig — auch an
				Mitschüler:innen und auch, wenn sie schon einmal bestellt haben. Immer Häkchen,
				immer Abmeldelink.</span>
			</div>`
	},
	{
		no: 10,
		title: 'Messen und besser werden',
		// Konzeptfelder, die dieses Kapitel speist
		fields: [13, 14, 15],
		html: `<div class="chapter-top">
				<div class="chapter-head">
					<span class="chapter-no">10</span>
					<div>
							<h2>Messen und besser werden</h2>
							<p class="chapter-sub">Omnichannel, KI-Werkzeuge, Kennzahlen und der Kreislauf aus Feedback und Test.</p>
					</div>
				</div>
				<figure class="art">
				<picture>
					<source srcset="${ILLUSTRATIONEN[10].avif}" type="image/avif" />
					<img src="${ILLUSTRATIONEN[10].webp}" alt="Zwei Jugendliche vor Diagrammen, eine zeigt den Daumen nach oben" />
				</picture>
			</figure>
			</div>

			<div class="goals">
				<p class="lbl">Nach diesem Kapitel könnt ihr</p>
				<ul>
					<li>Online- und Offline-Verkauf sinnvoll verbinden</li>
					<li>fünf Kennzahlen benennen und sagen, welche Entscheidung sie auslösen</li>
					<li>einen A/B-Test planen, dessen Ergebnis etwas bedeutet</li>
				</ul>
			</div>

			<h3>Omnichannel: Schulmesse trifft Onlineshop</h3>
			<p>Junior Companies verkaufen fast nie nur online. Es gibt den Stand beim Elternabend,
			die Weihnachtsmesse, den Pop-up-Tisch in der Aula. Omnichannel heißt, dass diese Wege
			zusammenarbeiten statt nebeneinanderher zu laufen.</p>
			<ul class="bullets">
				<li><b>Click &amp; Collect:</b> online bestellen, in der Schule abholen. Spart Porto und bringt Leute ins Haus.</li>
				<li><b>QR-Code am Stand:</b> führt direkt zum Shop oder zur Newsletter-Anmeldung — wer nicht bar zahlen will, bestellt später.</li>
				<li><b>Ein Lagerstand für alles.</b> Was am Messestand verkauft wurde, muss im Shop verschwinden, sonst verkauft ihr zweimal dasselbe Stück.</li>
				<li><b>Fotos vom Stand</b> werden zu Social-Media-Inhalten und Presseanhängen.</li>
			</ul>

			<h3>KI sinnvoll einsetzen</h3>
			<p>KI-Werkzeuge helfen bei Entwürfen, nicht beim Denken. Was funktioniert: erste
			Fassungen von Produkttexten, Ideen für Beitragsthemen, Übersetzungen, ein Chatbot für
			Standardfragen, Bildfreisteller für Produktfotos. Was nicht funktioniert: erfundene
			Fakten, Texte ohne Bezug zu eurer Company — und genau die erkennt das Bewertungsraster
			als „generische KI-Texte" mit 1 bis 3 Punkten.</p>

			<div class="note">
				<span class="ico i-merke">MERKE</span>
				<span>Wenn euer Konzepttext auch für jede andere Junior Company Österreichs passen
				würde, ist er zu allgemein. Namen, Zahlen, Orte und eigene Beispiele hinein — das
				ist der Unterschied zwischen 3 und 9 Punkten.</span>
			</div>

			<h3>Die fünf Kennzahlen, die reichen</h3>
			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 210" role="img" aria-label="Fünf Kennzahlen mit Beispielwert und der Entscheidung, die sie auslösen">
						<g class="svg-label">
							<g>
								<rect x="2" y="8" width="134" height="88" rx="10" fill="var(--surface-2)"></rect>
								<text x="16" y="30" class="svg-small svg-faint" letter-spacing="1">CONVERSION RATE</text>
								<text x="16" y="58" class="svg-ink" font-family="Poppins" font-size="22" font-weight="600">4,0 %</text>
								<text x="16" y="78" class="svg-small svg-soft">Wie gut der Shop</text>
								<text x="16" y="90" class="svg-small svg-soft">überzeugt</text>
							</g>
							<g>
								<rect x="144" y="8" width="134" height="88" rx="10" fill="var(--surface-2)"></rect>
								<text x="158" y="30" class="svg-small svg-faint" letter-spacing="1">Ø WARENKORB</text>
								<text x="158" y="58" class="svg-ink" font-family="Poppins" font-size="22" font-weight="600">20 €</text>
								<text x="158" y="78" class="svg-small svg-soft">Was eine Bestellung</text>
								<text x="158" y="90" class="svg-small svg-soft">einbringt</text>
							</g>
							<g>
								<rect x="286" y="8" width="134" height="88" rx="10" fill="var(--surface-2)"></rect>
								<text x="300" y="30" class="svg-small svg-faint" letter-spacing="1">CTR DER ANZEIGE</text>
								<text x="300" y="58" class="svg-ink" font-family="Poppins" font-size="22" font-weight="600">1,8 %</text>
								<text x="300" y="78" class="svg-small svg-soft">Ob die Anzeige</text>
								<text x="300" y="90" class="svg-small svg-soft">interessiert</text>
							</g>
							<g>
								<rect x="428" y="8" width="134" height="88" rx="10" fill="var(--surface-2)"></rect>
								<text x="442" y="30" class="svg-small svg-faint" letter-spacing="1">KOSTEN JE KUNDE</text>
								<text x="442" y="58" class="svg-ink" font-family="Poppins" font-size="22" font-weight="600">3,30 €</text>
								<text x="442" y="78" class="svg-small svg-soft">Werbebudget ÷ neue</text>
								<text x="442" y="90" class="svg-small svg-soft">Kund:innen</text>
							</g>
							<g>
								<rect x="570" y="8" width="128" height="88" rx="10" fill="var(--surface-2)"></rect>
								<text x="584" y="30" class="svg-small svg-faint" letter-spacing="1">RETOURENQUOTE</text>
								<text x="584" y="58" class="svg-ink" font-family="Poppins" font-size="22" font-weight="600">6 %</text>
								<text x="584" y="78" class="svg-small svg-soft">Ob Produkt und</text>
								<text x="584" y="90" class="svg-small svg-soft">Text passen</text>
							</g>

							<line x1="2" y1="116" x2="698" y2="116" stroke="var(--line)"></line>
							<text x="2" y="140" class="svg-small svg-faint" letter-spacing="1">JEDE ZAHL MUSS EINE ENTSCHEIDUNG AUSLÖSEN — SONST IST SIE NUR DEKORATION</text>
							<g class="svg-small svg-soft">
								<text x="2" y="164">Conversion niedrig → Kassa vereinfachen, Versandkosten früher zeigen</text>
								<text x="2" y="182">Warenkorb niedrig → Cross-Sell einbauen, Gratisversand ab Betrag X</text>
								<text x="2" y="200">CTR niedrig → anderes Bild, anderer Text</text>
								<text x="370" y="164">Kosten je Kunde über Deckungsbeitrag → stoppen</text>
								<text x="370" y="182">Retouren hoch → Beschreibung und Fotos prüfen</text>
								<text x="370" y="200">Alles stabil → nächsten Test starten</text>
							</g>
						</g>
					</svg>
				</div>
				<figcaption>Fünf Zahlen genügen. Ein Dashboard mit dreißig Kennzahlen, das niemand liest, ist wertlos.</figcaption>
			</figure>

			<h3>Feedback einsammeln</h3>
			<ul class="bullets">
				<li><b>Bewertung erbitten</b> — automatische Mail sieben Tage nach Zustellung, ein Klick auf Sterne.</li>
				<li><b>Retourengründe abfragen</b> — ein Pflichtfeld mit vier Auswahlmöglichkeiten reicht.</li>
				<li><b>Kurzumfrage nach dem Kauf</b> — eine einzige Frage: „Was hätte dich fast vom Kauf abgehalten?"</li>
				<li><b>Negatives sichtbar beantworten.</b> Eine gut beantwortete Ein-Stern-Bewertung wirkt vertrauensbildender als zwanzig Fünf-Sterne-Bewertungen.</li>
			</ul>

			<h3>A/B-Test: eine Änderung, zwei Gruppen</h3>
			<p>Beim A/B-Test bekommt eine Hälfte der Besucher:innen Variante A, die andere Hälfte
			Variante B. Nach ein paar Tagen zeigt sich, welche mehr Bestellungen bringt. Drei
			Regeln: <b>nur eine Sache ändern</b>, <b>lang genug laufen lassen</b>, und <b>vorher
			festlegen</b>, welche Zahl entscheidet.</p>

			<figure class="reveal">
				<div class="frame">
					<svg viewBox="0 0 700 170" role="img" aria-label="A/B-Test mit zwei Varianten eines Bestellknopfs und dem Ergebnis">
						<g class="svg-label">
							<rect x="4" y="18" width="340" height="132" rx="11" fill="var(--surface-2)"></rect>
							<text x="24" y="44" class="svg-small svg-faint" letter-spacing="1">VARIANTE A — 500 BESUCHE</text>
							<rect x="24" y="58" width="176" height="34" rx="8" fill="var(--ink-faint)"></rect>
							<text x="112" y="80" text-anchor="middle" fill="#fff" font-weight="600">In den Warenkorb</text>
							<text x="24" y="120" class="svg-ink" font-family="Poppins" font-size="19" font-weight="600">18 Bestellungen</text>
							<text x="24" y="140" class="svg-small svg-soft">3,6 % Conversion Rate</text>

							<rect x="356" y="18" width="340" height="132" rx="11" fill="var(--ok-wash)"></rect>
							<text x="376" y="44" class="svg-small svg-faint" letter-spacing="1">VARIANTE B — 500 BESUCHE</text>
							<rect x="376" y="58" width="220" height="34" rx="8" fill="var(--accent)"></rect>
							<text x="486" y="80" text-anchor="middle" fill="#fff" font-weight="600">Jetzt sichern — Versand gratis</text>
							<text x="376" y="120" class="svg-ink" font-family="Poppins" font-size="19" font-weight="600">27 Bestellungen</text>
							<text x="376" y="140" class="svg-small" fill="var(--ok)" font-weight="600">5,4 % Conversion Rate · +50 %</text>
						</g>
					</svg>
				</div>
				<figcaption>Zwischen A und B unterscheidet sich nur die Beschriftung des Knopfs — deshalb lässt sich der Unterschied im Ergebnis auch ihr zuordnen.</figcaption>
			</figure>

			${FALL_DECATHLON}`
	}
]
